import type { WizardStep } from "@/hooks/useWizardFlow";
import { firstStepAfterFinancial } from "@/hooks/useWizardFlow";
import { isPediatricByDob } from "@/utils/intakeHelpers";

/**
 * Pure routing decisions for the financial-responsibility / insurance sub-flow.
 *
 * Flow (independent of portalCaseHistoryFlow, which only affects the final handoff):
 *   Q1 (accident/work/neither):
 *     car-accident → Auto Insurance form
 *     work-injury  → Workers' Comp form
 *     neither      → Q2
 *   Auto form  / Workers' Comp form (Continue or Skip) → Q2
 *   Q2 (health insurance / no insurance):
 *     health-insurance → Health Insurance form
 *     no-insurance     → HANDOFF (planned history)
 *   Health form (Continue or Skip) → HANDOFF (planned history)
 *
 * Keeping these as pure functions makes every branch unit-testable without a browser.
 */

export const FINANCIAL_ROUTES = {
  commPrefs: "/complaint/communication-preferences",
  q1: "/complaint/financial-responsibility",
  q2: "/complaint/financial-responsibility-insurance",
  auto: "/complaint/auto-insurance",
  workersComp: "/complaint/workers-compensation",
  health: "/complaint/health-insurance",
  primaryComplaint: "/complaint/primary-complaint",
  pediatricHistory: "/complaint/pediatric-history",
} as const;

export type AccidentWorkChoice = "car-accident" | "work-injury" | "neither";
export type InsuranceChoice = "health-insurance" | "no-insurance";

export interface HandoffContext {
  /** True for portal-config history tasks: hand off to the already-planned history. */
  portalCaseHistoryFlow: boolean;
  /** Wizard steps built from the patient's config (drives firstStepAfterFinancial). */
  wizardSteps: WizardStep[];
  /** Patient DOB (legacy flow only, to pick pediatric vs adult complaint). */
  patientDob?: string | null;
}

export interface NavResult {
  /** Where to navigate. */
  path: string;
  /**
   * When true the caller must call primaryComplaintStore.startNewComplaint() before
   * navigating (legacy complaint hand-off only). Mirrors the pre-existing behaviour.
   */
  startComplaint: boolean;
}

/** Q1 answer → next route. Always routes through the forms (flag-independent). */
export function q1Route(choice: AccidentWorkChoice): string {
  if (choice === "car-accident") return FINANCIAL_ROUTES.auto;
  if (choice === "work-injury") return FINANCIAL_ROUTES.workersComp;
  return FINANCIAL_ROUTES.q2;
}

/**
 * Hand off to whatever history the patient's config already planned.
 * - Portal-config flow: firstStepAfterFinancial(). Guards against a "/" dead-end
 *   (a config with no history steps) by falling back to the complaint page.
 * - Legacy flow: start a new complaint, then pediatric or adult complaint page.
 * Never touches / re-plans the history itself.
 */
export function financialHandoff(ctx: HandoffContext): NavResult {
  if (ctx.portalCaseHistoryFlow) {
    const next = firstStepAfterFinancial(ctx.wizardSteps);
    return {
      path: next && next !== "/" ? next : FINANCIAL_ROUTES.primaryComplaint,
      startComplaint: false,
    };
  }
  const pediatric = !!ctx.patientDob && isPediatricByDob(ctx.patientDob);
  return {
    path: pediatric ? FINANCIAL_ROUTES.pediatricHistory : FINANCIAL_ROUTES.primaryComplaint,
    startComplaint: true,
  };
}

/** Q2 answer → next route. health-insurance → form; no-insurance → hand off. */
export function q2Route(choice: InsuranceChoice, ctx: HandoffContext): NavResult {
  if (choice === "health-insurance") {
    return { path: FINANCIAL_ROUTES.health, startComplaint: false };
  }
  return financialHandoff(ctx);
}
